import { useCallback, type ReactNode } from 'react'
import { router } from '@inertiajs/react'
import { SAVE_NETWORK_SENTENCE, SaveRefusedError, usePricingReview, type ReviewResponse } from '@/hooks/usePricingReview'
import type { HistoryEntry, UndoReviewToken } from '@/types/history'

/**
 * UNDO, FROM AN ENTRY OF A HOTEL'S HISTORY (docs/plans/2026-10-20-change-safety.md §5.11, §5.17.2,
 * R3.1 to R3.5).
 *
 * An undo is a pricing save like any other, so it goes through the same review: REVIEW-UI's
 * `usePricingReview` and its `PricingReviewDialog`, one implementation of the review wherever a
 * save happens (EP-36). This file only says where the review comes from and what confirming sends.
 *
 *   1. OPEN. The review is the server's `undo.reviewHref`, posted with an empty body: the server
 *      rehearses the undo's own save and rolls it back. The dialog shows its rows, and when someone
 *      changed one of those settings since, the conflict panel above them ("These settings changed
 *      after this entry"), the server's `confirmOverwriteLabel` and its `confirmTone` (rose when an
 *      undo would overwrite a later change). Refusals (403, 404, 422) come back as `errors[]` and
 *      show as the dialog's refused state, under the hotel and business when the server names them.
 *   2. CONFIRM. An Inertia POST to the server's `undo.undoHref` with the review's `reviewToken`,
 *      sent back untouched, and `confirm_overwrite: true` exactly when the review listed conflicts.
 *      The page keeps its state and scroll.
 *   3. THE REPLY is a 302 back to the Changes tab. With the notice, the undo happened: the dialog
 *      closes, the flash names the hotel, and the entry reads "Undone by …". With `errors.undo`, it
 *      did not: the dialog keeps the sentence in its rose panel and reviews again underneath it, so
 *      the next confirm is made against what is true now (another undo won, or a later change
 *      arrived, "Something changed since you reviewed this undo. Review it again.").
 *   4. FOCUS goes back to the Undo button when the dialog closes without undoing (the dialog returns
 *      focus to its opener, and the button takes focus as it opens it). After an undo the button is
 *      gone, so focus moves to the entry's own toggle.
 *
 * Nothing here composes a sentence, compares a value or builds a URL: both hrefs are the server's.
 */

// The dialog's close is a 150ms fade (operator.css): focus moves once it has gone.
const FOCUS_AFTER_CLOSE_MS = 200

type UndoReview = ReviewResponse & UndoReviewToken

export interface UndoController {
  /** Opens the review of undoing this entry. Does nothing unless the server offers Undo on it. */
  start: (entry: HistoryEntry) => void
  /** Render once, anywhere in the page. */
  dialog: ReactNode
}

export function useUndo(): UndoController {
  const review = usePricingReview()
  const { open } = review

  const start = useCallback(
    (entry: HistoryEntry) => {
      if (entry.undo.state !== 'available') return
      const { reviewHref, undoHref } = entry.undo
      open(reviewHref, {}, (reviewed) => postUndo(undoHref, reviewed as UndoReview, entry.id))
    },
    [open]
  )

  return { start, dialog: review.dialog }
}

function postUndo(undoHref: string, review: UndoReview, entryId: number): Promise<void> {
  const data: Record<string, string | boolean> = { confirm_overwrite: (review.conflicts ?? []).length > 0 }
  if (typeof review.reviewToken === 'string') data.review_token = review.reviewToken

  return new Promise<void>((resolve, reject) => {
    let settled = false
    const settle = (outcome: () => void) => {
      if (settled) return
      settled = true
      outcome()
    }

    // A reply that is not an Inertia page (a 403 when the person's role changed after the review, a
    // 500) would otherwise open Inertia's error modal over the dialog. Instead the dialog keeps the
    // save sentence and reviews again: the review endpoint answers a refusal in the server's words.
    const stopListening = router.on('invalid', () => {
      settle(() => reject(new SaveRefusedError([SAVE_NETWORK_SENTENCE], { refreshReview: true })))
      return false
    })

    router.post(undoHref, data, {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () =>
        settle(() => {
          resolve()
          window.setTimeout(() => focusEntry(entryId), FOCUS_AFTER_CLOSE_MS)
        }),
      onError: (errors) =>
        settle(() => {
          const sentence = errors.undo ?? Object.values(errors)[0] ?? SAVE_NETWORK_SENTENCE
          reject(new SaveRefusedError([sentence], { refreshReview: true }))
        }),
      onFinish: () => {
        stopListening()
        // Cancelled, or failed without a reply: nothing was undone, and the dialog says so.
        settle(() => reject(new SaveRefusedError([SAVE_NETWORK_SENTENCE], { refreshReview: true })))
      },
    })
  })
}

/** After an undo the entry's Undo button is gone: its own toggle takes focus, unless something has it. */
function focusEntry(entryId: number) {
  const active = document.activeElement
  if (active && active !== document.body && active.isConnected) return
  const toggle = document.querySelector<HTMLElement>(`[data-entry="${entryId}"] [data-entry-toggle]`)
  toggle?.focus()
}
